<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ShopsAPI from 'dashboard/api/shops';
import WebsiteLayout from '../components/WebsiteLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ShopForm from '../components/ShopForm.vue';
import TablePagination from '../components/TablePagination.vue';
import ResponsiveTable from '../components/ResponsiveTable.vue';

const { t } = useI18n();

const shops = ref([]);
const loading = ref(false);
const showForm = ref(false);
const selectedShop = ref(null);

// Header controls
const searchQuery = ref('');
const showFilterMenu = ref(false);
const showSortMenu = ref(false);
const filterActive = ref(null);
const sortField = ref(null);
const sortOrder = ref('asc');

// Pagination
const currentPage = ref(1);
const itemsPerPage = ref(25);

const headerTitle = computed(() => t('WEBSITE.HEADER.SHOPS_TITLE'));

// Filtrage et tri (sans pagination)
const filteredAndSortedShops = computed(() => {
  let result = [...shops.value];

  // Recherche
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(
      shop =>
        shop.name.toLowerCase().includes(query) ||
        shop.slug?.toLowerCase().includes(query) ||
        shop.description?.toLowerCase().includes(query)
    );
  }

  // Filtre actif/inactif
  if (filterActive.value !== null) {
    result = result.filter(shop => shop.active === filterActive.value);
  }

  // Tri
  if (sortField.value) {
    result.sort((a, b) => {
      let aVal = a[sortField.value];
      let bVal = b[sortField.value];

      if (sortField.value === 'updated_at') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (aVal < bVal) return sortOrder.value === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder.value === 'asc' ? 1 : -1;
      return 0;
    });
  }

  return result;
});

// Pagination
const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value);

const endIndex = computed(() =>
  Math.min(
    startIndex.value + itemsPerPage.value,
    filteredAndSortedShops.value.length
  )
);

// Résultats paginés (utilisé pour l'affichage)
const paginatedShops = computed(() => {
  return filteredAndSortedShops.value.slice(startIndex.value, endIndex.value);
});

// États
const isEmpty = computed(() => !loading.value && shops.value.length === 0);

const hasNoSearchResults = computed(
  () =>
    !loading.value &&
    shops.value.length > 0 &&
    filteredAndSortedShops.value.length === 0 &&
    (searchQuery.value || filterActive.value !== null)
);

// Reset pagination when filters change
watch([searchQuery, filterActive, sortField, itemsPerPage], () => {
  currentPage.value = 1;
});

const fetchShops = async () => {
  loading.value = true;
  try {
    const response = await ShopsAPI.get();
    shops.value = response.data;
  } catch (error) {
    useAlert(t('WEBSITE.SHOPS.API.ERROR.FETCH_FAILED'));
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchShops();
});

const openCreateForm = () => {
  selectedShop.value = null;
  showForm.value = true;
};

const openEditForm = shop => {
  selectedShop.value = shop;
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  selectedShop.value = null;
};

const handleSave = async () => {
  await fetchShops();
  closeForm();
};

const deleteShop = async shop => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm(t('WEBSITE.SHOPS.CONFIRM.DELETE'))) return;

  try {
    await ShopsAPI.delete(shop.id);
    useAlert(t('WEBSITE.SHOPS.API.SUCCESS.SHOP_DELETED'));
    await fetchShops();
  } catch (error) {
    useAlert(t('WEBSITE.SHOPS.API.ERROR.DELETE_FAILED'));
  }
};

const copyUrl = async url => {
  try {
    await navigator.clipboard.writeText(url);
    useAlert(t('WEBSITE.SHOPS.URL_COPIED'));
  } catch (error) {
    useAlert(t('WEBSITE.SHOPS.URL_COPY_FAILED'));
  }
};

const formatDate = dateString => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString();
};

// Fermer tous les menus sauf celui spécifié
const closeAllMenusExcept = exceptMenu => {
  if (exceptMenu !== 'filter') showFilterMenu.value = false;
  if (exceptMenu !== 'sort') showSortMenu.value = false;
};

// Gestion filtres et tri
const handleFilter = value => {
  filterActive.value = value;
  showFilterMenu.value = false;
};

const handleSort = field => {
  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortField.value = field;
    sortOrder.value = 'asc';
  }
  showSortMenu.value = false;
};

const clearSearch = () => {
  searchQuery.value = '';
  filterActive.value = null;
  sortField.value = null;
  sortOrder.value = 'asc';
  currentPage.value = 1;
};
</script>

<template>
  <WebsiteLayout :header-title="headerTitle">
    <template #actions>
      <Button
        :label="t('WEBSITE.SHOPS.EMPTY_STATE.BUTTON_LABEL')"
        icon="i-lucide-plus"
        size="sm"
        @click="openCreateForm"
      />
    </template>

    <!-- Loading -->
    <div
      v-if="loading"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center justify-center py-24 rounded-lg bg-gradient-to-br from-n-solid-1 to-n-slate-2 dark:to-n-slate-1 border-2 border-dashed border-n-weak"
    >
      <div
        class="mb-6 w-20 h-20 rounded-full bg-n-blue-2 dark:bg-n-blue-3 flex items-center justify-center"
      >
        <span class="i-lucide-shopping-bag size-10 text-n-blue-10" />
      </div>
      <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
        {{ t('WEBSITE.SHOPS.EMPTY_STATE.TITLE') }}
      </h3>
      <p class="text-sm text-center text-n-slate-10 max-w-md mb-6">
        {{ t('WEBSITE.SHOPS.EMPTY_STATE.MESSAGE') }}
      </p>
      <Button
        :label="t('WEBSITE.SHOPS.EMPTY_STATE.BUTTON_LABEL')"
        icon="i-lucide-plus"
        size="sm"
        @click="openCreateForm"
      />
    </div>

    <!-- Header bar avec search, filtres, tri -->
    <div
      v-else-if="!isEmpty"
      class="mb-3 bg-n-solid-1 border border-n-weak/70 rounded-lg shadow-sm"
    >
      <div class="flex items-center gap-2 px-2.5 py-1.5">
        <!-- Search bar -->
        <div class="flex-1 max-w-sm">
          <Input
            v-model="searchQuery"
            type="search"
            :placeholder="t('WEBSITE.SHOPS.LIST.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'h-8 [&:not(.focus)]:!border-transparent [&:not(.focus)]:!outline-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8 !text-xs',
            ]"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-3.5 top-1/2 ltr:left-2 rtl:right-2 pointer-events-none"
              />
            </template>
          </Input>
        </div>

        <!-- Séparateur vertical -->
        <div class="h-6 w-px bg-n-weak/40" />

        <!-- Filtres, Tri -->
        <div class="flex items-center gap-1.5">
          <!-- Filtre -->
          <div class="relative">
            <button
              class="px-2.5 py-1.5 bg-n-solid-1 border border-n-weak/50 text-n-slate-11 text-xs hover:bg-n-blue-2 hover:text-n-blue-11 hover:border-n-blue-6 transition-all flex items-center gap-1.5 rounded-lg font-medium"
              :class="{
                'bg-n-blue-3 text-n-blue-11 border-n-blue-6':
                  filterActive !== null,
              }"
              @click="
                closeAllMenusExcept('filter');
                showFilterMenu = !showFilterMenu;
              "
            >
              <span class="i-lucide-filter size-3.5" />
              <span>{{ t('WEBSITE.SHOPS.LIST.FILTER') }}</span>
              <span
                v-if="filterActive !== null"
                class="w-1.5 h-1.5 bg-n-blue-9 rounded-full animate-pulse"
              />
            </button>

            <div
              v-if="showFilterMenu"
              v-on-clickaway="() => (showFilterMenu = false)"
              class="absolute right-0 mt-1.5 w-56 bg-n-solid-1 border border-n-weak/60 shadow-2xl rounded-xl z-[100] py-2.5 overflow-hidden"
            >
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11': filterActive === null,
                }"
                @click="handleFilter(null)"
              >
                <span class="font-medium">{{
                  t('WEBSITE.SHOPS.LIST.ALL_SHOPS')
                }}</span>
                <span
                  v-if="filterActive === null"
                  class="i-lucide-check size-3.5 text-n-blue-11"
                />
              </button>
              <div class="h-px bg-n-weak/30 my-2 mx-3" />
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-teal-2 text-n-teal-11': filterActive === true,
                }"
                @click="handleFilter(true)"
              >
                <span class="font-medium flex items-center gap-2.5">
                  <span
                    class="w-2 h-2 rounded-full bg-n-teal-9 group-hover:scale-110 transition-transform"
                  />
                  {{ t('WEBSITE.SHOPS.LIST.ACTIVE') }}
                </span>
                <span
                  v-if="filterActive === true"
                  class="i-lucide-check size-3.5 text-n-teal-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-slate-2 text-n-slate-11': filterActive === false,
                }"
                @click="handleFilter(false)"
              >
                <span class="font-medium flex items-center gap-2.5">
                  <span
                    class="w-2 h-2 rounded-full bg-n-slate-9 group-hover:scale-110 transition-transform"
                  />
                  {{ t('WEBSITE.SHOPS.LIST.INACTIVE') }}
                </span>
                <span
                  v-if="filterActive === false"
                  class="i-lucide-check size-3.5 text-n-slate-11"
                />
              </button>
            </div>
          </div>

          <!-- Tri -->
          <div class="relative">
            <button
              class="px-2.5 py-1.5 bg-n-solid-1 border border-n-weak/50 text-n-slate-11 text-xs hover:bg-n-blue-2 hover:text-n-blue-11 hover:border-n-blue-6 transition-all flex items-center gap-1.5 rounded-lg font-medium"
              :class="{
                'bg-n-blue-3 text-n-blue-11 border-n-blue-6': sortField,
              }"
              @click="
                closeAllMenusExcept('sort');
                showSortMenu = !showSortMenu;
              "
            >
              <span class="i-lucide-arrow-up-down size-3.5" />
              <span>{{ t('WEBSITE.SHOPS.LIST.SORT') }}</span>
              <span
                v-if="sortField"
                class="w-1.5 h-1.5 bg-n-blue-9 rounded-full animate-pulse"
              />
            </button>

            <div
              v-if="showSortMenu"
              v-on-clickaway="() => (showSortMenu = false)"
              class="absolute right-0 mt-1.5 w-60 bg-n-solid-1 border border-n-weak/60 shadow-2xl rounded-xl z-[100] py-2.5 overflow-hidden"
            >
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11': sortField === 'name',
                }"
                @click="handleSort('name')"
              >
                <span class="flex items-center gap-2.5 font-medium">
                  <span
                    class="i-lucide-type size-3.5 group-hover:scale-110 transition-transform"
                  />
                  <span>{{ t('WEBSITE.SHOPS.LIST.SORT_NAME') }}</span>
                </span>
                <span
                  v-if="sortField === 'name'"
                  :class="
                    sortOrder === 'asc'
                      ? 'i-lucide-arrow-up'
                      : 'i-lucide-arrow-down'
                  "
                  class="size-3.5 text-n-blue-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11': sortField === 'updated_at',
                }"
                @click="handleSort('updated_at')"
              >
                <span class="flex items-center gap-2.5 font-medium">
                  <span
                    class="i-lucide-calendar size-3.5 group-hover:scale-110 transition-transform"
                  />
                  <span>{{ t('WEBSITE.SHOPS.LIST.SORT_DATE') }}</span>
                </span>
                <span
                  v-if="sortField === 'updated_at'"
                  :class="
                    sortOrder === 'asc'
                      ? 'i-lucide-arrow-up'
                      : 'i-lucide-arrow-down'
                  "
                  class="size-3.5 text-n-blue-11"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No search results -->
    <div
      v-if="hasNoSearchResults"
      class="flex flex-col items-center justify-center py-16 bg-n-solid-1 border border-n-weak/70 rounded-lg"
    >
      <span class="i-lucide-search-x mb-4 text-n-slate-9 size-12" />
      <h3 class="text-base font-semibold text-center text-n-slate-12 mb-2">
        {{ t('WEBSITE.SHOPS.NO_RESULTS.TITLE') }}
      </h3>
      <p class="text-sm text-center text-n-slate-10 max-w-md mb-6">
        {{ t('WEBSITE.SHOPS.NO_RESULTS.SUBTITLE') }}
      </p>
      <Button
        :label="t('WEBSITE.SHOPS.NO_RESULTS.CLEAR_BUTTON')"
        icon="i-lucide-x"
        size="sm"
        variant="outline"
        color="slate"
        @click="clearSearch"
      />
    </div>

    <!-- Shops Table -->
    <div
      v-else-if="!isEmpty && !hasNoSearchResults"
      class="w-full overflow-hidden border rounded-lg border-n-weak/70 bg-n-solid-1 shadow-sm min-h-[400px]"
    >
      <ResponsiveTable>
        <table class="w-full">
          <thead class="bg-n-alpha-1/50 border-b border-n-weak/60">
            <tr>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-store size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.SHOPS.LIST.NAME') }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-link size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.SHOPS.LIST.URL') }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-tag size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.SHOPS.LIST.CATEGORIES') }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-activity size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.SHOPS.LIST.STATUS') }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-calendar size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.SHOPS.LIST.UPDATED') }}</span>
                </div>
              </th>
              <th
                class="sticky right-0 px-5 py-3 text-xs font-semibold text-right tracking-wide text-n-slate-11 bg-n-alpha-1/50 backdrop-blur-sm shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]"
              >
                <div class="flex items-center justify-end gap-1.5">
                  <span>{{ t('WEBSITE.SHOPS.LIST.ACTIONS') }}</span>
                  <span class="i-lucide-more-horizontal size-3.5 opacity-30" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="bg-n-solid-1">
            <tr
              v-for="shop in paginatedShops"
              :key="shop.id"
              class="transition-all duration-150 ease-out border-t border-n-weak/40 hover:bg-n-blue-2/50 group"
            >
              <td class="px-5 py-3">
                <div class="flex flex-col">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ shop.name }}
                  </span>
                  <span v-if="shop.description" class="text-xs text-n-slate-10">
                    {{ shop.description }}
                  </span>
                  <!-- Statistiques inline -->
                  <div v-if="shop.stats" class="flex items-center gap-3 mt-1.5">
                    <span
                      class="inline-flex items-center gap-1 text-xs text-n-slate-10"
                    >
                      <span class="i-lucide-package size-3" />
                      {{ shop.stats.total_products }}
                    </span>
                    <span
                      class="inline-flex items-center gap-1 text-xs text-n-slate-10"
                    >
                      <span class="i-lucide-layers size-3" />
                      {{ shop.stats.categories_count }}
                    </span>
                    <span
                      class="inline-flex items-center gap-1 text-xs text-n-slate-10"
                    >
                      <span class="i-lucide-box size-3" />
                      {{ shop.stats.total_stock }}
                    </span>
                  </div>
                </div>
              </td>
              <td class="px-5 py-3">
                <div class="flex items-center gap-2">
                  <code class="text-xs text-n-slate-10">
                    {{ shop.full_url }}
                  </code>
                  <button
                    class="p-1 rounded hover:bg-n-alpha-2"
                    :title="t('WEBSITE.SHOPS.COPY_URL')"
                    @click="copyUrl(shop.full_url)"
                  >
                    <span class="i-lucide-copy size-3.5 text-n-slate-11" />
                  </button>
                </div>
              </td>
              <td class="px-5 py-3">
                <div
                  v-if="
                    shop.product_categories &&
                    shop.product_categories.length > 0
                  "
                  class="flex flex-wrap gap-1"
                >
                  <span
                    v-for="category in shop.product_categories"
                    :key="category.id"
                    class="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded bg-n-alpha-2 text-n-slate-11"
                  >
                    <img
                      v-if="category.logo && category.logo.url"
                      :src="category.logo.url"
                      :alt="category.name"
                      class="w-4 h-4 rounded object-cover"
                    />
                    <span v-else class="i-lucide-tag size-3" />
                    {{ category.name }}
                  </span>
                </div>
                <span
                  v-else-if="shop.categories && shop.categories.length > 0"
                  class="text-xs text-n-slate-10"
                >
                  {{ t('WEBSITE.SHOPS.LIST.ALL_PRODUCTS') }}
                </span>
                <span v-else class="text-xs text-n-slate-10">
                  {{ t('WEBSITE.SHOPS.LIST.ALL_PRODUCTS') }}
                </span>
              </td>
              <td class="px-5 py-3">
                <div
                  class="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium rounded-full border whitespace-nowrap transition-all duration-200"
                  :class="[
                    shop.active
                      ? 'bg-n-teal-2 text-n-teal-11 border-n-teal-6'
                      : 'bg-n-slate-2 text-n-slate-11 border-n-slate-6',
                  ]"
                >
                  <div
                    class="w-1.5 h-1.5 rounded-full"
                    :class="[
                      shop.active
                        ? 'bg-n-teal-9 animate-pulse'
                        : 'bg-n-slate-9',
                    ]"
                  />
                  {{
                    shop.active
                      ? t('WEBSITE.SHOPS.LIST.ACTIVE')
                      : t('WEBSITE.SHOPS.LIST.INACTIVE')
                  }}
                </div>
              </td>
              <td class="px-5 py-3 text-sm text-n-slate-10">
                {{ formatDate(shop.updated_at) }}
              </td>
              <td
                class="sticky right-0 px-5 py-3 bg-n-solid-1 backdrop-blur-sm shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]"
              >
                <div class="flex justify-end gap-2">
                  <button
                    class="flex items-center justify-center w-8 h-8 rounded-md bg-n-blue-3 hover:bg-n-blue-4 text-n-blue-11 hover:text-n-blue-12 border border-n-blue-6 hover:border-n-blue-7 transition-all duration-200 shadow-sm hover:shadow-md"
                    :title="t('WEBSITE.SHOPS.FORM.EDIT')"
                    @click="openEditForm(shop)"
                  >
                    <span class="i-lucide-edit size-4" />
                  </button>
                  <button
                    class="flex items-center justify-center w-8 h-8 rounded-md bg-n-ruby-3 hover:bg-n-ruby-4 text-n-ruby-11 hover:text-n-ruby-12 border border-n-ruby-6 hover:border-n-ruby-7 transition-all duration-200 shadow-sm hover:shadow-md"
                    :title="t('WEBSITE.SHOPS.FORM.DELETE')"
                    @click="deleteShop(shop)"
                  >
                    <span class="i-lucide-trash-2 size-4" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </ResponsiveTable>
    </div>

    <!-- Pagination Footer -->
    <div
      v-if="
        !isEmpty && !hasNoSearchResults && filteredAndSortedShops.length > 0
      "
      class="mt-6"
    >
      <TablePagination
        :current-page="currentPage"
        :total-items="filteredAndSortedShops.length"
        :items-per-page="itemsPerPage"
        @update:current-page="currentPage = $event"
        @update:items-per-page="itemsPerPage = $event"
      />
    </div>

    <!-- Shop Form Drawer -->
    <ShopForm
      v-if="showForm"
      :shop="selectedShop"
      @close="closeForm"
      @save="handleSave"
    />
  </WebsiteLayout>
</template>
